<template>
  <div class="pm">
    <!-- Loading -->
    <div v-if="loading && !plan" class="pm-state">
      <i class="fa fa-circle-o-notch fa-spin"></i>
      <p>Loading plan…</p>
    </div>

    <!-- Plan gone: the office deleted or re-saved this plan in Open Dental. A calm message, not the raw
         "Request failed with status code 404" error state; the way forward is the dashboard. -->
    <div v-else-if="builder.planGone" class="pm-state pm-state-error" data-test="pm-plan-gone">
      <i class="fa fa-exclamation-circle"></i>
      <h2>We couldn't load this treatment plan</h2>
      <p>It may have been deleted or changed in Open Dental. Choose another plan for this patient from the dashboard.</p>
      <button class="pm-exit-btn" data-test="pm-plan-gone-back" @click="onBackToDashboard">
        <i class="fa fa-arrow-left"></i> Back to dashboard
      </button>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pm-state pm-state-error">
      <i class="fa fa-exclamation-circle"></i>
      <h2>Couldn't load this plan</h2>
      <p>{{ error }}</p>
      <button class="pm-exit-btn" @click="onExit">
        <i class="fa fa-arrow-left"></i> Back to Builder
      </button>
    </div>

    <!-- Loaded -->
    <template v-else-if="plan">
      <!-- Top bar — intentionally minimal; the TC Controls tray (Slice 9b)
           will take over most chrome. For 9a we keep just enough to anchor
           the patient + plan context and provide an Exit handle. -->
      <header class="pm-topbar">
        <!-- Left: practice brand, with patient + plan stacked beneath it. -->
        <div class="pm-topbar-left">
          <div class="pm-topbar-brand">
            <img
              v-if="practiceLogoUrl"
              :src="practiceLogoUrl"
              :alt="practiceName"
              class="pm-practice-logo-large"
            />
            <template v-else>
              <div class="pm-practice-logo">{{ practiceInitial }}</div>
              <div class="pm-practice-meta">
                <div class="pm-practice-name">{{ practiceName }}</div>
                <div v-if="practiceTagline" class="pm-practice-tag">{{ practiceTagline }}</div>
              </div>
            </template>
          </div>
          <div class="pm-topbar-patient" v-if="patient">
            <div class="pm-patient-name">{{ patient.fName }} {{ patient.lName }}</div>
            <div class="pm-plan-heading">{{ plan.heading || 'Treatment plan' }}</div>
            <!-- Photo Attachments — the patient-facing way in. Same label and
                 same gallery the patient sees on their own link, so the TC is
                 walking them through the exact thing they'll get at home. -->
            <button
              v-if="showPhotos && visiblePlanPhotos.length > 0"
              class="pm-photos-btn"
              type="button"
              @click="galleryOpen = true"
            >
              <i class="fa fa-camera"></i>
              Photos of Planned Teeth
              <span class="pm-photos-count">{{ visiblePlanPhotos.length }}</span>
            </button>
          </div>
        </div>

        <!-- Right: actions on top, the plan totals right below them — so the
             totals sit in line with the patient name, aligned to the right. -->
        <div class="pm-topbar-right">
          <div class="pm-topbar-actions">
            <!-- Pull procedures added in Open Dental mid-appointment. Compact
                 wording on purpose: the patient can see this bar, and the
                 result text ("2 procedures added") is for the TC, not them.
                 Nothing auto-refreshes here — prices shifting unbidden in
                 front of a patient is worse than prices being stale. -->
            <RefreshPlanButton
              compact
              :loading="loading"
              :last-sync-at="builder.lastSyncAt"
              :outcome="builder.refreshOutcome"
              :failed-at="refreshFailedAt"
              @refresh="onRefreshFromOd"
            />
            <!-- Photo Attachments. Available regardless of the showPhotos
                 toggle — a TC chairside may well be taking the photo now and
                 deciding whether to show it a moment later. -->
            <button
              class="pm-photos-attach-btn"
              @click="photosModalOpen = true"
              title="Attach intra-oral photos to this patient"
            >
              <i class="fa fa-camera"></i> Attach Photos
            </button>
            <button class="pm-print-btn" @click="onPrint" title="Print this plan">
              <i class="fa fa-print"></i> Print
            </button>
            <button class="pm-exit-btn" @click="onExit" title="Exit Present Mode (Esc)">
              <i class="fa fa-times"></i> Exit
            </button>
          </div>

          <!-- Plan totals — two-column grid (label | amount) so the dollar
               figures line up on the right. Pulses for 1s after an alt swap
               to draw the eye to the financial change. -->
          <div class="pm-total" :class="{ 'pm-total-pulse': totalPulsing }">
            <span class="pm-total-k pm-total-k-head">Total</span>
            <span class="pm-total-v pm-total-v-head">{{ formatMoney(pricingLayers.membership && totalDiscount > 0 ? totalFee + totalDiscount : totalFee) }}</span>

            <template v-if="pricingLayers.membership && totalDiscount > 0">
              <span class="pm-total-k">Discount</span>
              <span class="pm-total-v pm-total-v-credit">−{{ formatMoney(totalDiscount) }}</span>
            </template>

            <template v-if="totalInsuranceSecondary > 0">
              <span class="pm-total-k">Primary ins.</span>
              <span class="pm-total-v pm-total-v-credit">−{{ formatMoney(totalInsurancePrimary) }}</span>
              <span class="pm-total-k">Secondary ins.</span>
              <span class="pm-total-v pm-total-v-credit">−{{ formatMoney(totalInsuranceSecondary) }}</span>
            </template>
            <template v-else-if="totalInsurance > 0">
              <span class="pm-total-k">Insurance</span>
              <span class="pm-total-v pm-total-v-credit">−{{ formatMoney(totalInsurance) }}</span>
            </template>

            <span class="pm-total-k">Patient</span>
            <span class="pm-total-v pm-total-v-patient">{{ formatMoney(patientPortion) }}</span>

            <template v-if="showMonthly && totalFee > 0">
              <span class="pm-total-k">Monthly</span>
              <span class="pm-total-v pm-total-v-monthly">{{ formatMoney(monthlyPayment) }}/mo · {{ monthlyTerm }} mo</span>
            </template>
          </div>
        </div>
      </header>

      <!-- Slice 10a #8 — concurrent-edit conflict banner. Mirrors the
           Builder's banner so a chairside TC isn't blindsided when a
           teammate edits the plan in the office between alt swaps. -->
      <div v-if="conflict" class="pm-conflict-banner">
        <i class="fa fa-exclamation-circle"></i>
        <div class="pm-conflict-body">
          <div class="pm-conflict-title">{{ conflict.message }}</div>
          <div class="pm-conflict-sub">Your last action wasn't saved.</div>
        </div>
        <div class="pm-conflict-actions">
          <button class="pm-conflict-btn" type="button" @click="onAdoptConflict">
            <i class="fa fa-refresh"></i> View their changes
          </button>
          <button class="pm-conflict-close" type="button" @click="onDismissConflict" aria-label="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Lens switcher. Only renders when 2+ patient views are enabled; the
           plan totals moved up into the topbar (right-aligned under the
           actions), so a single-view plan needs no toolbar bar at all. -->
      <div v-if="enabledViewOptions.length > 1" class="pm-toolbar">
        <div class="pm-lens-switcher">
          <button
            v-for="v in enabledViewOptions"
            :key="v.id"
            class="pm-lens-tab"
            :class="{ active: activeView === v.id }"
            @click="activeView = v.id"
          >{{ v.label }}</button>
        </div>
      </div>

      <!-- View canvas — same 3 components the Builder + Patient View use.
           Slice 9c — wrapped in <transition mode="out-in"> for the lens
           crossfade. Leave finishes before enter starts so the layouts
           don't overlap mid-swap (each view has different vertical rhythm
           and the overlap reads as a glitch).
           Slice 9d — alt swap enabled on Itemized + Arch & Region views.
           PhaseJourney flattens alts to chips so swap-from-chip doesn't
           translate well; TC switches lens to swap. -->
      <main class="pm-canvas">
        <transition name="pm-lens" mode="out-in">
          <component
            :is="activeViewComponent"
            :key="activeViewComponent"
            :procedures="procedures"
            :alts-by-primary="altsByPrimary"
            :label-fn="resolveLabel"
            :reason-fn="resolveReason"
            :alt-swap-enabled="true"
            :show-prices="currentViewShowsPrices"
            :pricing-layers="pricingLayers"
            :group-by-priority="itemizedGroupByPriority"
            :group-label="itemizedGroupLabel"
            :show-group-subtotals="itemizedGroupSubtotals"
            :photo-teeth="photoTeeth"
            @swap-alt="onSwapAlt"
            @open-tooth-photos="onOpenToothPhotos"
          />
        </transition>

        <!-- Plan-level financial summary. Chairside the TC walks the
             patient through this on the way to discussing payment options
             below. Gated on ShowsTotals, NOT ShowsPrices — in 'total' mode
             this card is the only money on screen, which is the point. -->
        <PlanFinancialSummary
          :procedures="procedures"
          :show-prices="currentViewShowsTotals"
          :show-discount="pricingLayers.membership"
          :show-balance="showBalance"
          :family-balance="familyBalance"
          :selected-option="chosenPaymentOption"
          :payment-dates="chosenPaymentDates"
          :downpayment="chosenDownpayment"
          :cherry-terms="cherryTerms"
          :cherry-as-of-text="cherryAsOfText"
          :cherry-updating="cherryTermsUpdating"
        />

        <!-- Payment options — selectable chairside. Each card shows the dollar
             breakdown against the patient portion; tap to mark the chosen plan
             (reflects live in the Plan Summary above). Renders when the tray
             toggle is on. -->
        <transition name="pm-payments">
          <section v-if="showPaymentOptions && paymentOptionsToShow.length > 0" class="pm-payments">
            <div class="pm-payments-header">
              <h3 class="pm-payments-title">Payment options</h3>
              <span v-if="paymentHelperNote" class="pm-payments-helper">{{ paymentHelperNote }}</span>
            </div>
            <div class="pm-payments-grid">
              <div
                v-for="opt in paymentOptionsToShow"
                :key="opt.id"
                class="pm-payment-card"
                :class="{ recommended: opt.recommended, chosen: opt.id === chosenPaymentOptionId }"
              >
                <div v-if="opt.id === chosenPaymentOptionId" class="pm-payment-chosen-badge">
                  <i class="fa fa-check-circle"></i> Chosen
                </div>
                <div v-else-if="opt.recommended" class="pm-payment-badge">Most popular</div>
                <div class="pm-payment-title">{{ opt.title }}</div>
                <div class="pm-payment-desc">{{ paymentDesc(opt) }}</div>

                <!-- Multi-tier financing menu — always shown under the card (like
                     the patient view) so the patient can evaluate the terms before
                     choosing. Deliberately INDEPENDENT of currentViewShowsTotals:
                     the tiers ARE the offer, not a price overlay. The card's own
                     price block (down in .pm-payment-foot) is the part that
                     follows the money gate. -->
                <div
                  v-if="optBreakdown(opt).kind === 'finance' && (optBreakdown(opt).tiers || []).length"
                  class="pm-finance-tiers"
                >
                  <div v-for="(t, ti) in optBreakdown(opt).tiers" :key="ti" class="pm-finance-tier">
                    <span class="pm-finance-tier-label">{{ tierLabel(t) }}</span>
                    <span class="pm-finance-tier-amt">~{{ formatMoney(Math.round(t.monthly)) }}/mo</span>
                  </div>
                </div>

                <!-- A Cherry-backed option's menu: the lender's REAL plans for this exact
                     amount, or no figures at all (its breakdown has no typed tiers, so the
                     block above renders nothing for it). Same placement and money-gate rule
                     as the typed menu - the plans ARE the offer. -->
                <CherryTierList
                  v-if="isCherryBacked(opt)"
                  variant="present"
                  :opt="opt"
                  :terms="cherryTerms"
                  :amount="optCherryAmount(opt)"
                  :as-of-text="cherryAsOfText"
                  :updating="cherryTermsUpdating"
                />

                <div v-if="opt.terms" class="pm-payment-terms">{{ opt.terms }}</div>

                <!-- Additional fee (read-only on Present Mode; set per-patient
                     in the Builder). Shown on every card the office has the
                     feature on, so the patient sees the full picture before
                     they pick. -->
                <div v-if="optFeeLabel(opt)" class="pm-payment-fee">
                  <i class="fa fa-plus-circle"></i>
                  <span class="pm-payment-fee-label">{{ optFeeLabel(opt) }}</span>
                  <span class="pm-payment-fee-amt">+{{ formatMoney(optFeeAmount(opt)) }}</span>
                </div>

                <!-- Card foot — the money, the CTA and (when chosen) the
                     chairside rows. Anchored to the bottom of the card
                     (margin-top:auto) so the dollar figures and the buttons line
                     up across a row however much prose, how many finance tiers
                     or what fee each card happens to carry. -->
                <div class="pm-payment-foot">
                  <!-- This option's dollar amount: headline + qualifying
                       sub-line, both from the ONE shared helper that also
                       renders the patient-at-home card and the signed PDF, so
                       the three can never quote the same plan differently.

                       Gated on currentViewShowsTotals — the same gate the Plan
                       Summary above uses. It was gated on `showPrices` from
                       2026-05-24 until 2026-09-13: an identifier that has never
                       existed in this component, so the block silently never
                       rendered, chairside or on paper. -->
                  <div v-if="currentViewShowsTotals && optAmountLines(opt)" class="pm-payment-math">
                    <span class="pm-pay-math-amt">{{ optAmountLines(opt).headline }}</span>
                    <span v-if="optAmountLines(opt).sub" class="pm-pay-math-sub">{{ optAmountLines(opt).sub }}</span>
                  </div>

                  <!-- Call-to-action — uses the practice's configured button
                       language (Settings → Payment Options). Marks this the
                       chosen plan; click again to clear. -->
                  <button
                    class="pm-payment-cta"
                    :class="{ active: opt.id === chosenPaymentOptionId }"
                    type="button"
                    @click="onChoosePaymentOption(opt.id)"
                  >
                    <i class="fa" :class="opt.id === chosenPaymentOptionId ? 'fa-check-circle' : 'fa-circle-o'"></i>
                    {{ opt.id === chosenPaymentOptionId ? 'Chosen' : paymentButtonLanguage }}
                  </button>

                  <!-- Chairside "Pay today" downpayment + optional per-payment
                       due dates, for the chosen option.

                       Pay today shows for ANY option the office enabled it on —
                       split, in-house AND financing. It previously lived inside a
                       `kind === 'split'` gate, which silently hid it for every
                       financing option (prod 2026-08-04). Due dates stay
                       split-only: financing has tiers, not discrete due dates.

                       Deliberately ONE container rather than two siblings —
                       .pm-payment-dates carries a dashed border-top and
                       .pm-payment-paytoday-row a dashed border-bottom, so
                       splitting them would double the rules and the spacing. -->
                  <div
                    v-if="opt.id === chosenPaymentOptionId && (canTakeDownpayment(opt) || optBreakdown(opt).kind === 'split')"
                    class="pm-payment-dates"
                  >
                    <div
                      v-if="canTakeDownpayment(opt)"
                      class="pm-payment-date-row pm-payment-paytoday-row"
                    >
                      <label class="pm-payment-date-label">Pay today (optional)</label>
                      <div class="pm-payment-paytoday-input-wrap">
                        <span class="pm-payment-paytoday-prefix">$</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          class="pm-payment-date-input pm-payment-paytoday-input"
                          :value="chosenDownpayment || ''"
                          placeholder="0"
                          @input="onSetDownpayment($event.target.value)"
                        />
                      </div>
                    </div>
                    <!-- Caption sits INSIDE the split branch so it labels only the
                         date rows — above a financing downpayment it read as if
                         the amount were itself a due date. -->
                    <template v-if="optBreakdown(opt).kind === 'split'">
                      <div class="pm-payment-dates-caption">Payment due dates (optional)</div>
                      <div
                        v-for="n in optBreakdown(opt).count"
                        :key="n"
                        class="pm-payment-date-row"
                      >
                        <label class="pm-payment-date-label">Payment {{ n }}</label>
                        <input
                          type="date"
                          class="pm-payment-date-input"
                          :value="chosenPaymentDates[n - 1] || ''"
                          @change="onSetPaymentDate(n - 1, $event.target.value)"
                        />
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </transition>

        <!-- In-office signature. Revealed when the TC taps "Request signature
             on this device" in the tray; the patient signs on the same device.
             Reuses the patient-view SignaturePanel + the same sign flow, with
             an explicit shortId + a Builder-derived snapshot. -->
        <section v-if="signMode" ref="signSection" class="pm-sign pm-no-print">
          <div v-if="signSuccess" class="pm-sign-done">
            <i class="fa fa-check-circle"></i>
            <div class="pm-sign-done-body">
              <div class="pm-sign-done-title">Signed — sending to your office</div>
              <div class="pm-sign-done-sub">
                A signed copy is on its way to the Practice Management System.
              </div>
            </div>
            <button class="pm-sign-done-btn" type="button" @click="onCloseSign">Done</button>
          </div>
          <template v-else>
            <div class="pm-sign-bar">
              <span class="pm-sign-bar-label"><i class="fa fa-pencil-square-o"></i> Patient signature</span>
              <button class="pm-sign-cancel" type="button" @click="onCloseSign">Cancel</button>
            </div>
            <SignaturePanel
              :busy="signSubmitting"
              :busy-label="signBusyLabel"
              :error="signError"
              :hide-name="true"
              :default-name="patientFullName"
              :disclaimer="presentDisclaimer"
              @submit="onSignSubmit"
              @error="onSignPanelError"
            />
          </template>
        </section>

        <!-- Collect deposit — chairside surface. Muted before sign, active
             after. Reuses moolah store actions; backend writePaymentToOD
             routes through buildPrepaymentAllocation (intent='prepayment')
             so the OD PaySplit lands as a prepayment with the office's
             configured UnearnedType. -->
        <PresentDepositPanel
          v-if="paymentRemaining > 0 || payments.length"
          :site-i-d="siteID"
          :patient="patient"
          :treat-plan-num="treatPlanNum"
          :chosen-option="chosenPaymentOption"
          :suggested-amount="chosenDownpayment"
          :patient-portion="patientPortion"
          :payments="payments"
          :paid-total="paidTotal"
          :remaining="paymentRemaining"
          :max-payable="totalFee"
          :signed="signSuccess"
          :payment-provider="sitePaymentProvider"
          :existing="depositState"
          :text-to-pay-settings="textToPaySettings"
          :timezone="allSettings && allSettings.GENERAL_TIMEZONE ? allSettings.GENERAL_TIMEZONE.PropertyValue : ''"
          @save-state="onDepositSaveState"
          @skip="onDepositSkip"
          @deposit-collected="onDepositCollected"
          @payment-collected="onPaymentCollected"
        />
      </main>

      <!-- Slice 9b — Floating TC Controls tray. The tray owns the chairside
           control surface; the inline lens switcher in the top toolbar is
           kept for now as a quick alternative (and as the entry-state when
           tray is collapsed). -->
      <TCControlsTray
        class="pm-no-print"
        :active-view="activeView"
        :view-options="enabledViewOptions"
        :internal-note="internalNote"
        :note-saving="noteSaving"
        :show-monthly="showMonthly"
        :monthly-term="monthlyTerm"
        :show-payment-options="showPaymentOptions"
        :view-pricing-modes="viewPricingModes"
        :pricing-layers="pricingLayers"
        :show-balance="showBalance"
        :show-photos="showPhotos"
        :patient-photo-count="patientPhotoCount"
        :plan-photo-count="planPhotoCount"
        :itemized-group-by-priority="itemizedGroupByPriority"
        :itemized-group-label="itemizedGroupLabel"
        :itemized-group-subtotals="itemizedGroupSubtotals"
        @update:active-view="activeView = $event"
        @update:internal-note="internalNote = $event"
        @update:show-monthly="showMonthly = $event"
        @update:monthly-term="monthlyTerm = $event"
        @update:show-payment-options="showPaymentOptions = $event"
        @set-pricing-mode="onSetPricingMode"
        @toggle-pricing-layer="onTogglePricingLayer"
        @toggle-show-balance="onToggleShowBalance"
        @toggle-show-photos="onToggleShowPhotos"
        @toggle-itemized-grouping="onToggleItemizedGrouping"
        @toggle-group-subtotals="onToggleGroupSubtotals"
        @set-group-label="onSetGroupLabel"
        @save-note="onSaveNote"
        @send-plan="onSendPlan"
        @request-signature="onRequestSignature"
      />

      <!-- Slice 9b — SendPlanModal mounts here too (also lives on Builder
           + PatientProfile). Store is the source of truth; this page just
           declares the mount via v-if on the open state. -->
      <SendPlanModal v-if="sendPlanOpen" :patient="patient" :timezone="allSettings && allSettings.GENERAL_TIMEZONE ? allSettings.GENERAL_TIMEZONE.PropertyValue : ''" />

      <!-- Photo Attachments — manager, patient-facing gallery, and the shared
           enlarged viewer. -->
      <PatientPhotosModal
        v-if="photosModalOpen && siteID && builder.patNum"
        :site-i-d="siteID"
        :pat-num="builder.patNum"
        :patient="patient"
        @close="photosModalOpen = false"
        @preview="onPreviewPhoto"
      />

      <PlanPhotosGallery
        v-if="galleryOpen"
        :photos="visiblePlanPhotos"
        @close="galleryOpen = false"
        @open="onOpenGalleryPhoto"
        @refresh-urls="onRefreshPhotoUrls"
      />

      <PhotoLightbox
        v-if="lightboxPhotos.length > 0"
        :photos="lightboxPhotos"
        :start-index="lightboxIndex"
        @close="lightboxPhotos = []"
        @refresh-urls="onRefreshPhotoUrls"
      />

      <!-- Slice 9d — Undo toast after an alt swap. Bottom-LEFT so it
           doesn't compete visually with the FABs at bottom-right. Auto-
           dismisses after 6s; tap Undo to restore the pre-swap state. -->
      <transition name="pm-undo">
        <div v-if="undoState" class="pm-undo-toast" role="status">
          <i class="fa fa-exchange"></i>
          <span class="pm-undo-text">
            Switched to <strong>{{ undoState.newLabel }}</strong>
          </span>
          <button class="pm-undo-btn" type="button" @click="onUndoSwap">
            Undo
          </button>
          <button class="pm-undo-close" type="button" @click="dismissUndoToast" aria-label="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </transition>

      <!-- Slice 10a #2 — Swap save-failure toast. Same position + shape
           as the undo toast but red-themed for the error semantic. Stacks
           ABOVE the undo toast if both fire near-simultaneously (rare). -->
      <transition name="pm-undo">
        <div v-if="swapError" class="pm-swap-error-toast" role="alert">
          <i class="fa fa-exclamation-triangle"></i>
          <span class="pm-undo-text">{{ swapError }}</span>
          <button class="pm-undo-close" type="button" @click="dismissSwapErrorToast" aria-label="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </transition>

    </template>
  </div>
</template>

<script>
/**
 * Slice 9a — Present Mode shell.
 *
 * Full-screen in-office presentation surface. Reuses the same view
 * components the Builder + Patient View render (ItemizedView,
 * ArchRegionView) so the chairside walkthrough matches what the TC
 * configured and what the patient sees later at home.
 *
 * Entry: "Present in-office" button on the Builder top bar
 * Exit:  X button top-right OR Esc key → navigates back to the Builder
 *        route for the same plan. The Builder reloads cleanly on re-entry;
 *        no state-preservation gymnastics needed.
 *
 * Slice 9b+ will add: floating TC Controls tray (FAB), lens crossfade
 * animation, inline alt swap, financial toggles, monthly slider. This file
 * stays the layout host; the tray + behaviors are additive on top.
 */
import { mapState, mapActions, mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import ItemizedView from './components/views/ItemizedView.vue';
import ArchRegionView from './components/views/ArchRegionView.vue';
import PlanFinancialSummary from './components/PlanFinancialSummary.vue';
import TCControlsTray from './components/TCControlsTray.vue';
import SendPlanModal from './components/SendPlanModal.vue';
import SignaturePanel from '@/pages/TxPatientView/components/SignaturePanel.vue';
import PresentDepositPanel from './components/PresentDepositPanel.vue';
import RefreshPlanButton from './components/RefreshPlanButton.vue';
import { interpolatePaymentDesc, computePaymentBreakdown, overlayRecommendedFromSettings, resolveFeeAmount, formatFeeLabel, financeTierLabel, canTakeDownpayment, paymentOptionAmountLines, cherryPricingFor } from './utils/paymentOptions.js';
import { isCherryBacked, cherryAmountFor, cherryAsOfText } from './utils/cherryTerms.js';
import { patientPortion as sumPatientPortion } from './utils/patientPortion.js';
import cherryTermsSync from './mixins/cherryTermsSync.js';
import CherryTierList from './components/CherryTierList.vue';
import { resolvePricingDisplay, buildPricingModePatch } from './utils/resolveDisplay.js';
import { insuranceSplit } from './utils/insuranceSplit.js';
import { resolveEnabledViews, resolveDefaultView } from './constants/views.js';
import { resolvePatientDisclaimer } from './utils/disclaimer.js';
import PatientPhotosModal from './components/photos/PatientPhotosModal.vue';
import PlanPhotosGallery from './components/photos/PlanPhotosGallery.vue';
import PhotoLightbox from './components/photos/PhotoLightbox.vue';
import {
  planToothNums, filterPhotosForPlan, photoTeethMap, photosForTooth,
} from './utils/photoFilter.js';

export default {
  name: 'TxPresenterPresentMode',
  // cherryTermsSync re-prices a Cherry-backed option when the amount it finances changes (Pay
  // today edited, plan refreshed); it reads the `cherryPricing` computed below.
  mixins: [mixin, cherryTermsSync],
  components: { ItemizedView, ArchRegionView, PlanFinancialSummary, CherryTierList, TCControlsTray, SendPlanModal, SignaturePanel, PresentDepositPanel, RefreshPlanButton, PatientPhotosModal, PlanPhotosGallery, PhotoLightbox },

  data() {
    return {
      patient: null,
      plans: [],

      activeView: 'region',
      viewOptions: [
        { id: 'region', label: 'Arch & Region' },
        { id: 'itemized', label: 'Itemized' },
      ],

      escHandler: null,

      // Photo Attachments. `lightboxPhotos` non-empty IS the open state.
      photosModalOpen: false,
      galleryOpen: false,
      lightboxPhotos: [],
      lightboxIndex: 0,

      // Timestamp of the last failed refresh-from-OD; drives the refresh
      // button's transient error state. See onRefreshFromOd.
      refreshFailedAt: null,

      // Slice 9b — internal note state (mirrors Builder's pattern).
      // `internalNote` is the live edit buffer; `lastSavedNote` lets us
      // skip redundant saves when blur fires with no change.
      internalNote: '',
      lastSavedNote: '',
      noteSaving: false,

      // Slice 9d — alt-swap undo state.
      // `undoState` is either null or { previousAnnotations, newLabel }.
      // Auto-dismisses after UNDO_TIMEOUT_MS via undoTimer.
      undoState: null,
      undoTimer: null,
      totalPulsing: false,
      totalPulseTimer: null,
      UNDO_TIMEOUT_MS: 6000,
      TOTAL_PULSE_MS: 1000,

      // Slice 10a #2 — swap error toast state. Surfaces a save-failure
      // banner so the TC knows the swap didn't persist (store already
      // rolled back the local UI commit).
      swapError: null,
      swapErrorTimer: null,

      // Slice 9e — financial controls. All TC-toggled, all session-local
      // (no persistence — these are chairside walkthrough aids, not plan
      // configuration). Defaults: monthly OFF (TC opts in mid-conversation
      // when a financial question lands), 24-month term as a sensible
      // middle of the 6-60 range.
      showMonthly: false,
      monthlyTerm: 24,
      // Payment options shown by default chairside — the TC almost always
      // walks the patient through these, so don't make them toggle it on.
      showPaymentOptions: true,

      // In-office signature: shown by default chairside (the TC almost always
      // wants a signature), and also re-revealable via the tray's "Request
      // signature on this device". signShortId is the ensured short URL the
      // sign event keys off (pre-fetched on load in bootstrap).
      signMode: true,
      signShortId: null,
      signerNameDone: '',
      // Practice's TEXT_TO_PAY_SETTINGS_PAYMENT — drives the deposit panel's
      // method availability (manualMethodPayType.cash/check + prepaymentUnearnedType).
      // Loaded in bootstrap; the panel greys out CTAs when a slot isn't set.
      textToPaySettings: {},
    };
  },

  computed: {
    ...mapState('txPresenter', { builder: (s) => s.builder }),
    ...mapGetters('txPresenter', [
      'builderVisibleProcedures',
      'builderAltsByPrimary',
      'builderResolvedLabel',
      'builderResolvedReason',
      'settingsGeneralResolved',
      'settingsPresentationResolved',
    ]),

    loading() { return this.builder.loading; },
    error() { return this.builder.error; },
    plan() { return this.builder.plan; },
    procedures() { return this.builderVisibleProcedures || []; },
    altsByPrimary() { return this.builderAltsByPrimary || {}; },
    resolveLabel() { return (proc) => this.builderResolvedLabel(proc); },
    resolveReason() { return (proc) => this.builderResolvedReason(proc); },

    totalFee() {
      return this.procedures.reduce((sum, p) => sum + (p.fee || 0), 0);
    },

    // In-office sign flow state (shared sign machinery in the store).
    signState() { return this.$store.state.txPresenter.sign; },
    signSubmitting() { return this.signState.submitting; },
    signSuccess() { return this.signState.stage === 'done'; },
    signError() { return this.signState.error || ''; },
    signBusyLabel() { return 'Sending…'; },
    // Practice-wide insurance disclaimer + acknowledgement lines (Settings →
    // Presentation), exactly as the patient link receives them from the server:
    // null when the office has neither lines nor a visible paragraph, which leaves
    // the sign panel as it always was (built-in checkbox, no paragraph).
    presentDisclaimer() { return resolvePatientDisclaimer(this.settingsPresentationResolved); },

    activeViewComponent() {
      switch (this.activeView) {
        case 'itemized': return 'ItemizedView';
        case 'region':
        default: return 'ArchRegionView';
      }
    },

    // Enabled patient views (Settings → General). Switcher hides when only one.
    enabledViewOptions() {
      const enabled = resolveEnabledViews(this.settingsPresentationResolved);
      return this.viewOptions.filter((o) => enabled.includes(o.id));
    },

    // Two-level pricing — Present Mode mirrors the Builder: the per-patient
    // pricingDisplay (same Annotations source) layered over the practice
    // "house style" defaults (Settings → Presentation). What the TC set in the
    // right-pane Financials tab carries straight into the chairside view.
    resolvedDisplay() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      const defaults = (this.settingsPresentationResolved && this.settingsPresentationResolved.displayDefaults) || {};
      return resolvePricingDisplay(pd, defaults);
    },
    /** Per-view pricing mode: 'full' | 'total' | 'none'. See resolveDisplay.js. */
    viewPricingModes() {
      return this.resolvedDisplay.pricing;
    },
    /**
     * Layer toggles are global, NOT per-view — they say which sub-lines appear
     * wherever a view is 'full'. Which views show money is viewPricingModes.
     */
    pricingLayers() {
      return this.resolvedDisplay.layers;
    },
    /** PER-ROW fees in the active view. */
    currentViewShowsPrices() {
      return this.viewPricingModes[this.activeView] === 'full';
    },
    /**
     * The Plan Summary card — a separate gate, so 'total' can drop per-row fees
     * while keeping the plan total. Do not collapse these two back together.
     */
    currentViewShowsTotals() {
      return this.viewPricingModes[this.activeView] !== 'none';
    },
    itemizedGroupByPriority() {
      return this.resolvedDisplay.itemizedGroupByPriority;
    },
    itemizedGroupLabel() {
      return this.resolvedDisplay.itemizedGroupLabel;
    },
    itemizedGroupSubtotals() {
      return this.resolvedDisplay.itemizedGroupSubtotals;
    },
    /** "Show balance/credits from Open Dental" toggle (resolved). */
    showBalance() {
      return this.resolvedDisplay.showBalance;
    },

    // ─── Photo Attachments (mirrors the Builder's computeds) ─────────────
    showPhotos() {
      return this.resolvedDisplay.showPhotos;
    },
    patientPhotos() {
      return this.$store.state.txPresenter.patientPhotos.list;
    },
    patientPhotoCount() {
      return this.patientPhotos.length;
    },
    planTeeth() {
      return planToothNums(this.procedures);
    },
    visiblePlanPhotos() {
      return filterPhotosForPlan(this.patientPhotos, this.planTeeth);
    },
    planPhotoCount() {
      return this.visiblePlanPhotos.length;
    },
    /** Empty when the toggle is off — the views never learn a toggle exists. */
    photoTeeth() {
      if (!this.showPhotos) return {};
      return photoTeethMap(this.visiblePlanPhotos, this.planTeeth);
    },
    /** Guarantor account balance / credit (BalTotal − InsEst) from the load. */
    familyBalance() {
      return (this.builder && this.builder.familyBalance) || 0;
    },
    /** Balance folded into the patient portion — 0 when the toggle is off. */
    balanceAmount() {
      return this.showBalance ? (parseFloat(this.familyBalance) || 0) : 0;
    },

    site() {
      const sites = (this.$store.state.customer && this.$store.state.customer.sites) || [];
      return sites.find((s) => s.SiteID === this.siteID) || null;
    },
    practiceName() {
      const fromSettings = (this.settingsGeneralResolved && this.settingsGeneralResolved.practiceName) || '';
      if (fromSettings.trim()) return fromSettings;
      return (this.site && this.site.Name) || 'Practice';
    },
    practiceInitial() {
      const n = this.practiceName.trim();
      return (n.charAt(0) || 'P').toUpperCase();
    },
    practiceTagline() {
      return (this.settingsGeneralResolved && this.settingsGeneralResolved.practiceTagline) || '';
    },
    /** Practice logo (teamio.sites.LogoUrl) — mirrors the Builder header. */
    practiceLogoUrl() {
      return (this.site && this.site.LogoUrl) || '';
    },

    // Slice 9b — SendPlanModal mount gate.
    sendPlanOpen() { return this.$store.state.txPresenter.sendPlan.open; },
    // Slice 10a #8 — concurrent-edit conflict surface from the store.
    conflict() { return this.builder.conflict; },

    // ─── Slice 9e — Financial computeds ──────────────────────
    /**
     * Ballpark monthly payment for the plan total at the current term.
     * Uses simple division (total ÷ months), not an APR-based amortization
     * — patients get exact financing terms from their card / lender. This
     * is just a chairside conversation prop ("if we split this across 24
     * months, it's about $X/mo"). Rounded to whole dollars.
     */
    monthlyPayment() {
      const months = Math.max(1, this.monthlyTerm || 1);
      const m = (this.totalFee || 0) / months;
      return Math.round(m);
    },
    /**
     * Payment options shown chairside, enabled-only. Per-patient overrides
     * (PaymentOptionOverrides — includes any custom one-off options + edited
     * discounts/terms the TC set in the Builder) take precedence over the
     * practice defaults, so Present Mode matches exactly what the patient
     * sees at home. Falls back to settings (Slice 5 defaults) when no
     * per-patient overrides exist.
     */
    /**
     * Overlaid but UNFILTERED options. This is the materialization base any
     * time we write per-patient overrides back (see onSetDownpayment): writing
     * from the enabled-only list below would drop every DISABLED option out of
     * this patient's snapshot permanently — they'd vanish from the Builder and
     * could never be re-enabled for them.
     */
    paymentOptionsAll() {
      const settings = this.$store.getters['txPresenter/settingsPaymentResolved'];
      const settingsOptions = (settings && Array.isArray(settings.options)) ? settings.options : [];
      const overrides = this.builder && this.builder.paymentOptionOverrides;
      const base = Array.isArray(overrides) ? overrides : settingsOptions;
      // "Most Popular" always reflects live practice settings, never a stale
      // per-patient override snapshot.
      return overlayRecommendedFromSettings(base, settingsOptions);
    },
    paymentOptionsToShow() {
      return this.paymentOptionsAll.filter((o) => o.enabled);
    },
    paymentHelperNote() {
      const settings = this.$store.getters['txPresenter/settingsPaymentResolved'];
      return (settings && settings.helperNote) || '';
    },
    /** Practice's configured CTA button text (Settings → Payment Options). */
    paymentButtonLanguage() {
      const settings = this.$store.getters['txPresenter/settingsPaymentResolved'];
      return (settings && settings.buttonLanguage) || "I'm interested";
    },

    patientFullName() {
      const p = this.patient;
      if (!p) return '';
      return [p.fName, p.lName].filter(Boolean).join(' ').trim();
    },
    /**
     * Plan-level insurance estimate, split into primary/secondary via the
     * shared helper (explicit backend fields when present, else the combined
     * max(0, net − patient) fallback on primary). `totalInsurance` stays the
     * combined figure so the single "Insurance" topbar pair is unchanged for
     * single-insurance patients.
     */
    totalInsurancePrimary() {
      return this.procedures.reduce((s, p) => s + insuranceSplit(p).primary, 0);
    },
    totalInsuranceSecondary() {
      return this.procedures.reduce((s, p) => s + insuranceSplit(p).secondary, 0);
    },
    totalInsurance() {
      return this.totalInsurancePrimary + this.totalInsuranceSecondary;
    },
    /**
     * Plan-level OD procedure discount sum. Drives the topbar "Discount" pair
     * and the pre-discount "Total" when the discount layer is on. Zero when no
     * proc carries a discount → topbar renders exactly as before.
     */
    totalDiscount() {
      return this.procedures.reduce((s, p) => s + (parseFloat(p.discount) || 0), 0);
    },

    // ─── Payment selection (chairside) ───────────────────────
    /** Patient out-of-pocket the payment math computes against. Folds in the
     *  guarantor balance / credit when the showBalance toggle is on, so the
     *  topbar portion, Plan Summary green bar, and the Amount to Pay all agree. */
    patientPortion() {
      return sumPatientPortion(this.procedures, this.balanceAmount);
    },
    /**
     * The (single) enabled Cherry-backed option and the amount Cherry must price for it - the
     * SAME inputs optBreakdown uses (portion incl. the balance layer, the option's fee, and the
     * chosen option's Pay today), so what cherryTermsSync fetches is what the card matches.
     */
    cherryPricing() {
      return cherryPricingFor(this.paymentOptionsAll, this.patientPortion, this.chosenPaymentOptionId, this.chosenDownpayment);
    },
    /** Site timezone (teamio.settings GENERAL_TIMEZONE) for the Cherry "as of" stamp. */
    siteTimezone() {
      const s = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
      return (s && s.PropertyValue) || '';
    },
    /** "October 10, 2026 at 11:00 AM" - when Cherry priced the terms on screen, in the site's timezone. */
    cherryAsOfText() {
      return cherryAsOfText(this.cherryTerms, this.siteTimezone);
    },
    /** The chosen option id + due dates + Pay today, persisted in pricingDisplay. */
    chosenPaymentOptionId() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      return pd.chosenPaymentOptionId || null;
    },
    /** Chairside "Pay today" $ amount for the chosen option (per-plan). */
    chosenDownpayment() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      const n = parseFloat(pd.chosenDownpayment);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    chosenPaymentDates() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      return Array.isArray(pd.chosenPaymentDates) ? pd.chosenPaymentDates : [];
    },
    /** Resolved chosen option object (or null) — drives the Plan Summary breakdown. */
    chosenPaymentOption() {
      if (!this.chosenPaymentOptionId) return null;
      return this.paymentOptionsToShow.find((o) => o.id === this.chosenPaymentOptionId) || null;
    },
    /** "Pay toward balance" ledger (Slice 3) — paid payments for this plan. */
    payments() { return (this.builder && this.builder.payments) || []; },
    paidTotal() {
      const n = parseFloat(this.builder && this.builder.paidTotal);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    /** Remaining against the VISIBLE patient portion — the backend `remaining`
     *  sums ALL procedures (incl. hidden), so we compute it here from the
     *  visible patientPortion the TC actually sees. */
    paymentRemaining() {
      return Math.max(0, Math.round((this.patientPortion - this.paidTotal) * 100) / 100);
    },
    /** treatPlanNum from the route — passed to the deposit panel so payment
     *  rows are pinned to the right plan via ProviderMetadata. */
    treatPlanNum() {
      const n = parseInt(this.$route.params.treatPlanNum, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    sitePaymentProvider() {
      return (this.site && this.site.PaymentProvider) || '';
    },
    /** Existing deposit state from the plan's pricingDisplay (for re-open). */
    depositState() {
      const pd = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      return {
        depositPaymentID: pd.depositPaymentID || null,
        depositCollectedAt: pd.depositCollectedAt || null,
        depositMethod: pd.depositMethod || null,
        depositAmount: pd.depositAmount || 0,
        depositExternalRef: pd.depositExternalRef || null,
      };
    },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(newSite) {
        if (newSite) this.bootstrap();
      },
    },
    // Slice 9e — financial input changes pulse the total pill to draw
    // the eye to the recalculated number. Same animation pattern as 9d
    // alt-swap so the chairside motor memory is consistent.
    monthlyPayment() {
      if (this.showMonthly) this.triggerTotalPulse();
    },
    showMonthly(on) {
      if (on) this.triggerTotalPulse();
    },
  },

  mounted() {
    // Flag the body so the global print rule (Layout.scss) hides the fixed
    // left-nav sidebar while in Present Mode — covers both the Print button
    // and Ctrl+P. Removed on destroy so other pages' print is unaffected.
    document.body.classList.add('pm-print-host');

    // Keyboard exit — Esc returns to Builder.
    this.escHandler = (e) => {
      if (e.key !== 'Escape') return;
      this.onExit();
    };
    document.addEventListener('keydown', this.escHandler);
  },

  beforeDestroy() {
    document.body.classList.remove('pm-print-host');
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
    }
    if (this.totalPulseTimer) {
      clearTimeout(this.totalPulseTimer);
      this.totalPulseTimer = null;
    }
    if (this.swapErrorTimer) {
      clearTimeout(this.swapErrorTimer);
      this.swapErrorTimer = null;
    }
    // Don't call closeBuilder here — if the user is navigating back to
    // the Builder route the Builder will reload anyway, and if they're
    // navigating elsewhere the next page will set up its own context.
  },

  methods: {
    ...mapActions('txPresenter', [
      'loadBuilder', 'loadSettings', 'fetchPresentationDetail', 'refreshBuilderFromOd',
    ]),

    /**
     * Pull procedures added in Open Dental since this presentation opened.
     *
     * MUST be refreshBuilderFromOd, never loadBuilder — loadBuilder commits
     * RESET_BUILDER first, which would blank the chairside session with a
     * patient watching. refreshBuilderFromOd deliberately doesn't reset, and
     * the server hands back the persisted annotations, so the chosen payment
     * option, deposit state and alt selections all survive.
     */
    onRefreshFromOd() {
      this.refreshFailedAt = null;
      this.refreshBuilderFromOd().catch(() => {
        this.refreshFailedAt = Date.now();
      });
    },

    // Description token expansion — when this option is the chosen one,
    // inject the live "Pay today" amount so {downpayment} interpolates.
    paymentDesc(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : null;
      return interpolatePaymentDesc(opt, extras);
    },
    optBreakdown(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : undefined;
      return computePaymentBreakdown(opt, this.patientPortion, extras);
    },
    /**
     * The headline + sub-line of money printed ON the card. Same extras rule as
     * optBreakdown (only the chosen card sees the chairside "Pay today"), and
     * the same helper the patient-at-home view and the signed PDF use — the
     * wording lives in utils/paymentOptions.js, never in this template.
     * Returns null for cards that have no honest number to show.
     */
    optAmountLines(opt) {
      const extras = (opt && opt.id === this.chosenPaymentOptionId)
        ? { downpayment: this.chosenDownpayment }
        : undefined;
      return paymentOptionAmountLines(opt, this.patientPortion, extras, (n) => this.formatMoney(n), this.cherryTerms);
    },
    tierLabel(t) {
      return financeTierLabel(t);
    },
    /** Template access to the shared predicate — see utils/cherryTerms.js. */
    isCherryBacked(opt) {
      return isCherryBacked(opt);
    },
    /** The amount this Cherry card's terms must have been priced for (same breakdown as the card). */
    optCherryAmount(opt) {
      return cherryAmountFor(this.optBreakdown(opt));
    },
    /** Template access to the shared predicate — see utils/paymentOptions.js. */
    canTakeDownpayment(opt) {
      return canTakeDownpayment(opt);
    },
    // Per-card read-only Additional Fee preview ($ amount + label). Returns
    // empty/0 when the option doesn't have the fee feature on.
    optFeeAmount(opt) { return resolveFeeAmount(opt, this.patientPortion); },
    optFeeLabel(opt) { return this.optFeeAmount(opt) > 0 ? formatFeeLabel(opt) : ''; },

    /**
     * Mark (or unmark) a payment option as the chosen plan. Clicking the
     * already-chosen card clears it; switching to a different option resets
     * the per-payment due dates AND the Pay today amount from the previous
     * choice (they're tied to the option that's currently chosen).
     */
    onChoosePaymentOption(id) {
      if (id === this.chosenPaymentOptionId) {
        this.savePricingDisplay({ chosenPaymentOptionId: null, chosenPaymentDates: [], chosenDownpayment: 0 });
        return;
      }
      // Seed the plan-level amount from whatever the TC set for THIS option in
      // the Builder (0 when unset). Gated on the OVERLAID option so a stored
      // amount can't resurface after the office turns the feature off in
      // Settings. No existing plan has opt.downpayment, so for current data
      // this computes 0 — identical to the reset it replaces.
      const opt = this.paymentOptionsToShow.find((o) => o && o.id === id);
      const seed = canTakeDownpayment(opt) ? Math.max(0, parseFloat(opt.downpayment) || 0) : 0;
      this.savePricingDisplay({ chosenPaymentOptionId: id, chosenPaymentDates: [], chosenDownpayment: seed });
    },
    onSetPaymentDate(index, value) {
      const dates = [...this.chosenPaymentDates];
      while (dates.length <= index) dates.push('');
      dates[index] = value || '';
      this.savePricingDisplay({ chosenPaymentDates: dates });
    },
    /**
     * Chairside "Pay today" edit. Writes BOTH:
     *   - annotations.pricingDisplay.chosenDownpayment — the live plan-level
     *     value the tier math, deposit panel, patient view and PDF all read
     *   - the chosen option's per-patient `downpayment` — so the Builder shows
     *     it and re-choosing this option re-seeds it
     *
     * Both ride ONE saveAnnotations dispatch. Two dispatches would corrupt
     * state: the action commits all three columns back from the server
     * read-back, so the first response would revert the second's local commit
     * (the input visibly snapping back mid-typing), and if the second request
     * failed after the first succeeded the plan-level value would be lost while
     * the per-option value persisted — leaving the PDF disagreeing with the
     * Builder. The backend applies both in a single atomic upsert.
     */
    onSetDownpayment(raw) {
      const n = parseFloat(raw);
      const amount = Number.isFinite(n) && n > 0 ? n : 0;

      const annotations = { ...(this.builder.annotations || {}) };
      annotations.pricingDisplay = {
        ...(annotations.pricingDisplay || {}),
        chosenDownpayment: amount,
      };

      // Materialize from the UNFILTERED list — paymentOptionsToShow is
      // enabled-only, and writing that back would drop disabled options out of
      // this patient's snapshot for good.
      const all = JSON.parse(JSON.stringify(this.paymentOptionsAll));
      const idx = all.findIndex((o) => o && o.id === this.chosenPaymentOptionId);
      if (idx !== -1) all[idx] = { ...all[idx], downpayment: amount };

      this.$store.commit('txPresenter/SET_BUILDER_ANNOTATIONS', annotations);
      if (idx !== -1) {
        this.$store.commit('txPresenter/SET_BUILDER_PAYMENT_OPTION_OVERRIDES', all);
      }
      this.$store.dispatch(
        'txPresenter/saveAnnotations',
        idx !== -1 ? { annotations, paymentOptionOverrides: all } : { annotations }
      );
    },

    // ─── Deposit panel handlers ───────────────────────────
    /** Persist the new deposit state on the plan's pricingDisplay so a
     *  re-opened signed-and-paid plan shows the read-only audit line. */
    onDepositSaveState(state) {
      this.savePricingDisplay(state || {});
    },
    /** Skip — clear any in-flight deposit state for the plan. */
    onDepositSkip() {
      this.savePricingDisplay({
        depositPaymentID: null,
        depositCollectedAt: null,
        depositMethod: null,
        depositAmount: 0,
        depositExternalRef: null,
      });
    },
    /** Fired by the panel once a deposit successfully lands. Currently a
     *  hook for future analytics / toasts; the audit line is driven by the
     *  saved state, so nothing else needs to happen here. */
    onDepositCollected(/* { method, amount, paymentID } */) {
      // Hook — intentionally empty in v1.
    },
    /** After a payment lands, re-fetch the plan data so the ledger (payments +
     *  remaining) reflects it. The panel resets itself to idle for the next one. */
    async onPaymentCollected() {
      const patNum = parseInt(this.$route.params.patNum, 10);
      const treatPlanNum = parseInt(this.$route.params.treatPlanNum, 10);
      if (!patNum || !treatPlanNum || !this.siteID) return;
      try {
        await this.loadBuilder({ siteID: this.siteID, patNum, treatPlanNum });
      } catch (e) { /* in state */ }
    },

    // Print the plan (active view + summary + payment options). The @media
    // print stylesheet drops the chairside chrome (tray, toolbar, FABs).
    onPrint() { window.print(); },

    async bootstrap() {
      const patNum = parseInt(this.$route.params.patNum, 10);
      const treatPlanNum = parseInt(this.$route.params.treatPlanNum, 10);
      if (!patNum || !treatPlanNum || !this.siteID) return;

      // Sign state is global; clear any leftover 'done'/'error' from a prior
      // patient so the default-visible pad starts fresh for this plan.
      this.$store.dispatch('txPresenter/resetSign');
      this.signMode = true;
      this.signerNameDone = '';

      try {
        // Settings load in parallel; the default-view setting from General
        // applies on first paint if present.
        this.loadSettings({ siteID: this.siteID })
          .then(() => {
            this.activeView = resolveDefaultView(this.settingsPresentationResolved);
          })
          .catch(() => { /* in state */ });

        // Text-to-pay settings (Cash/Check + prepayment) — drives the deposit
        // panel's method availability. Best-effort: if it fails, the panel
        // greys out the corresponding CTAs with a "configure in Settings" hint.
        this.$store.dispatch('teamio/getTextToPaySettings', this.siteID)
          .then((res) => {
            this.textToPaySettings = (res && res.settings) || {};
          })
          .catch(() => { this.textToPaySettings = {}; });

        // Patient + plans header context.
        const detail = await this.fetchPresentationDetail({
          siteID: this.siteID, patNum, treatPlanNum,
        });
        this.patient = detail.patient;
        this.plans = detail.plans || [];
        // Slice 9b — seed the internal-note buffer from the persisted value.
        this.internalNote = detail.internalNote || '';
        this.lastSavedNote = detail.internalNote || '';

        // Builder workspace data — same shape Builder uses, so the views
        // render identically.
        await this.loadBuilder({ siteID: this.siteID, patNum, treatPlanNum });

        // Signature pad is visible by default chairside — pre-ensure a shortId
        // (idempotent, no SMS/email) so the sign flow is ready the instant the
        // patient signs, without the TC tapping "Request signature" first.
        this.$store.dispatch('txPresenter/ensureTxShortUrl', {
          siteID: this.siteID, patNum, treatPlanNum,
        }).then((shortId) => { this.signShortId = shortId; })
          .catch(() => { /* tray "Request signature" re-tries the ensure */ });

        // Photo Attachments — cached in the store per patient, so arriving
        // here from the Builder is usually a no-op. Loaded regardless of the
        // showPhotos toggle: the Attach Photos button works either way, and
        // the tray's counts have to be right before the TC flips it on.
        this.$store.dispatch('txPresenter/loadPatientPhotos', {
          siteID: this.siteID, patNum,
        });

        // Slice 6 — AI label/reason content. Non-blocking; fallbacks render
        // until content arrives.
        this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'label' }).catch(() => {});
        this.$store.dispatch('txPresenter/loadProcedureContent', { type: 'reason' }).catch(() => {});
      } catch (e) {
        // Slice 10a #10 — if fetchPresentationDetail failed, drawer.error is
        // set but builder.error stays null and the template renders blank
        // (loading=false, plan=null, error=null falls through all branches).
        // Mirror the error onto builder.error so the existing error state
        // path renders an actionable message + the Back-to-Builder button.
        console.error('[TxPresenterPresentMode] bootstrap error:', e);
        // Plan-gone has its own card (builder.planGone) — don't ALSO mirror the raw axios text onto the error state.
        const b = this.$store.state.txPresenter.builder;
        if (!b.error && !b.planGone) {
          this.$store.commit('txPresenter/SET_BUILDER_ERROR', e.message || 'Failed to load plan');
        }
      }
    },

    /** Plan-gone card: there is no plan to go back to in the Builder, so go to the dashboard. */
    onBackToDashboard() {
      this.$router.push({ name: 'TxPresenter' });
    },

    onExit() {
      const patNum = parseInt(this.$route.params.patNum, 10);
      const treatPlanNum = parseInt(this.$route.params.treatPlanNum, 10);
      if (patNum && treatPlanNum) {
        this.$router.push({
          name: 'TxPresenterBuilder',
          params: { patNum: String(patNum), treatPlanNum: String(treatPlanNum) },
        });
      } else {
        this.$router.push({ name: 'TxPresenter' });
      }
    },

    formatMoney(amt) {
      const n = typeof amt === 'number' ? amt : parseFloat(amt || 0);
      return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // ─── Slice 9b — TC Controls tray handlers ────────────────────
    /**
     * Save the internal note on tray-note blur. Mirrors Builder's
     * onSaveNote pattern (skip when unchanged, use saveInternalNote
     * action which reads state.drawer for the plan context).
     */
    async onSaveNote(noteText) {
      const trimmed = (noteText || '').trim();
      const last = (this.lastSavedNote || '').trim();
      if (trimmed === last) return;
      this.noteSaving = true;
      try {
        await this.$store.dispatch('txPresenter/saveInternalNote', {
          internalNote: trimmed || null,
          setBy: this.$store.state.customer.employeeID || null,
        });
        this.lastSavedNote = noteText;
      } catch (e) {
        // Error is surfaced in drawer.error; tray's "Unsaved changes"
        // indicator stays so the TC can retry by re-blurring.
      } finally {
        this.noteSaving = false;
      }
    },

    /**
     * Open the existing SendPlanModal. The store reads state.builder for
     * siteID/patNum/treatPlanNum (loaded by our bootstrap), so the same
     * flow that works on Builder + PatientProfile works here unchanged.
     */
    onSendPlan() {
      this.$store.dispatch('txPresenter/openSendPlan').catch(() => { /* in state */ });
    },

    // ─── In-office signature ────────────────────────────────────
    /**
     * Reveal the signature pad in the canvas so the patient can sign on this
     * device. Ensures a shortId exists (the sign endpoint keys off it), then
     * shows SignaturePanel + scrolls to it.
     */
    async onRequestSignature() {
      try {
        const { siteID, patNum, treatPlanNum } = this.builder;
        this.signShortId = await this.$store.dispatch('txPresenter/ensureTxShortUrl', {
          siteID, patNum, treatPlanNum,
        });
        this.$store.dispatch('txPresenter/resetSign');
        this.signerNameDone = '';
        this.signMode = true;
        this.$nextTick(() => {
          const el = this.$refs.signSection;
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        });
      } catch (e) {
        // Surface via the canvas error banner (mirror onError mapping).
        this.$store.commit('txPresenter/SET_SIGN_ERROR', e.message || 'Could not start signing.');
        this.signMode = true;
      }
    },
    async onSignSubmit({ signerName, signatureDataUrl, acknowledgedIds }) {
      this.signerNameDone = signerName;
      try {
        await this.$store.dispatch('txPresenter/submitSignFlow', {
          signerName,
          signatureDataUrl,
          acknowledgedIds,
          context: {
            shortId: this.signShortId,
            planSnapshot: {
              procedures: this.procedures,
              altsByPrimary: this.altsByPrimary,
              plan: this.plan,
              totalFee: this.totalFee,
              // Freeze the guarantor balance shown at signing (the showBalance
              // toggle resolves live at PDF render). Exact amount the patient saw
              // folded into their Estimated Patient Portion.
              familyBalance: this.familyBalance,
            },
            preferencesSnapshot: { selectedIds: [], otherNote: '' },
          },
        });
      } catch (e) { /* error already in sign state */ }
    },
    onSignPanelError(message) {
      this.$store.commit('txPresenter/SET_SIGN_ERROR', message || 'Something went wrong.');
    },
    onCloseSign() {
      this.signMode = false;
      this.$store.dispatch('txPresenter/resetSign');
    },

    // ─── Financial controls (persist to annotations.pricingDisplay,
    //     mirroring the Builder Financials tab) ────────────────────
    /**
     * Set one view's pricing mode: 'full' | 'total' | 'none'. Mirrors the
     * Builder's handler; buildPricingModePatch also writes the legacy
     * viewVisibility boolean (see its doc comment — don't drop it).
     */
    onSetPricingMode(view, mode) {
      // Raw blob, not resolvedDisplay — see the Builder's copy for why
      // (patching from the resolved object pins the untouched view).
      const raw = (this.builder.annotations && this.builder.annotations.pricingDisplay) || {};
      this.savePricingDisplay(buildPricingModePatch(raw, view, mode));
    },
    onTogglePricingLayer(key) {
      const next = { ...this.pricingLayers, [key]: !this.pricingLayers[key] };
      this.savePricingDisplay({ layers: next });
    },
    onToggleItemizedGrouping() {
      this.savePricingDisplay({ itemizedGroupByPriority: !this.itemizedGroupByPriority });
    },
    onToggleGroupSubtotals() {
      this.savePricingDisplay({ itemizedGroupSubtotals: !this.itemizedGroupSubtotals });
    },
    /** Chairside toggle for the Remaining Balance / Credits line. */
    onToggleShowBalance() {
      this.savePricingDisplay({ showBalance: !this.showBalance });
    },

    // ─── Photo Attachments ───────────────────────────────────────────────
    onToggleShowPhotos() {
      this.savePricingDisplay({ showPhotos: !this.showPhotos });
    },
    onOpenToothPhotos(toothNum) {
      const forTooth = photosForTooth(this.visiblePlanPhotos, toothNum);
      if (forTooth.length === 0) return;
      this.lightboxPhotos = forTooth;
      this.lightboxIndex = 0;
    },
    /** Gallery thumbnail → full size, keeping the gallery's own ordering. */
    onOpenGalleryPhoto(index) {
      this.lightboxPhotos = this.visiblePlanPhotos.slice();
      this.lightboxIndex = index || 0;
    },
    onPreviewPhoto(photo) {
      const idx = this.patientPhotos.findIndex((p) => p.photoId === photo.photoId);
      this.lightboxPhotos = this.patientPhotos.slice();
      this.lightboxIndex = idx === -1 ? 0 : idx;
    },
    /**
     * Presigned URLs aged out. Present Mode is the surface most likely to hit
     * this — a tablet handed to a patient can sit on one plan for an hour.
     */
    async onRefreshPhotoUrls() {
      if (!this.siteID || !this.builder.patNum) return;
      const openIds = this.lightboxPhotos.map((p) => p.photoId);
      await this.$store.dispatch('txPresenter/loadPatientPhotos', {
        siteID: this.siteID,
        patNum: this.builder.patNum,
        force: true,
      });
      if (openIds.length > 0) {
        this.lightboxPhotos = this.patientPhotos.filter((p) => openIds.indexOf(p.photoId) !== -1);
      }
    },
    onSetGroupLabel(word) {
      const w = (word == null ? '' : String(word)).trim();
      this.savePricingDisplay({ itemizedGroupLabel: w || 'Priority' });
    },
    // NOTE: this signature is a WHITELIST — a key absent from it is silently
    // dropped. That is how "Total only" shipped broken: buildPricingModePatch
    // emitted { pricing, viewVisibility } and only viewVisibility survived, so
    // the mode round-tripped through the legacy boolean and came back 'none'.
    // Adding a key to pricingDisplay means adding it HERE too; there is a test
    // pinning the patch builder's keys against this signature.
    savePricingDisplay({
      pricing, layers, viewVisibility, itemizedGroupByPriority, itemizedGroupLabel, itemizedGroupSubtotals, showBalance, showPhotos,
      chosenPaymentOptionId, chosenPaymentDates, chosenDownpayment,
      depositPaymentID, depositCollectedAt, depositMethod, depositAmount,
    } = {}) {
      const annotations = { ...(this.builder.annotations || {}) };
      const existing = annotations.pricingDisplay || {};
      annotations.pricingDisplay = {
        ...existing,
        ...(pricing ? { pricing } : {}),
        ...(layers ? { layers } : {}),
        ...(viewVisibility ? { viewVisibility } : {}),
        ...(itemizedGroupByPriority !== undefined ? { itemizedGroupByPriority } : {}),
        ...(itemizedGroupLabel !== undefined ? { itemizedGroupLabel } : {}),
        ...(itemizedGroupSubtotals !== undefined ? { itemizedGroupSubtotals } : {}),
        ...(showBalance !== undefined ? { showBalance } : {}),
        ...(showPhotos !== undefined ? { showPhotos } : {}),
        ...(chosenPaymentOptionId !== undefined ? { chosenPaymentOptionId } : {}),
        ...(chosenPaymentDates !== undefined ? { chosenPaymentDates } : {}),
        ...(chosenDownpayment !== undefined ? { chosenDownpayment } : {}),
        // Deposit-collection state — the chairside deposit panel writes these
        // when a payment is created (depositPaymentID) and again when it lands
        // 'paid' (depositCollectedAt + depositMethod + depositAmount). Lets a
        // re-opened signed-and-paid plan show the read-only audit line.
        ...(depositPaymentID !== undefined ? { depositPaymentID } : {}),
        ...(depositCollectedAt !== undefined ? { depositCollectedAt } : {}),
        ...(depositMethod !== undefined ? { depositMethod } : {}),
        ...(depositAmount !== undefined ? { depositAmount } : {}),
      };
      this.$store.commit('txPresenter/SET_BUILDER_ANNOTATIONS', annotations);
      this.$store.dispatch('txPresenter/saveAnnotations', { annotations });
    },

    // Slice 10a #8 — concurrent-edit conflict actions.
    onAdoptConflict() {
      this.$store.dispatch('txPresenter/adoptConflictingState');
    },
    onDismissConflict() {
      this.$store.dispatch('txPresenter/dismissConflict');
    },

    // ─── Slice 9d — alt swap + undo ─────────────────────────────
    /**
     * Tap-handler from the views. The store action atomically rewrites the
     * alt group with the new primary, saves, and returns a deep-cloned
     * snapshot of the pre-swap annotations for the undo path.
     *
     * We capture the label of the new primary BEFORE dispatch so the toast
     * shows what the TC chose even if the underlying data shifts (e.g.,
     * AI cache loads asynchronously).
     */
    async onSwapAlt({ altProcNum }) {
      // The view emits `primaryProcNum` too (current primary in the group),
      // but the store action doesn't need it — it derives the group from
      // the alt's own annotation. Kept in the event payload for future use
      // (e.g., telemetry or per-swap analytics).
      if (!altProcNum) return;
      // Resolve label from current builder state (procedures array
      // includes both primaries and alts).
      const proc = (this.builder.procedures || []).find((p) => p.procNum === altProcNum);
      const newLabel = proc ? this.resolveLabel(proc) : 'alternative';

      try {
        const result = await this.$store.dispatch('txPresenter/swapAltToPrimary', {
          newPrimaryProcNum: altProcNum,
        });
        if (!result) return; // safety: alt wasn't actually swappable
        this.showUndoToast({
          previousAnnotations: result.previousAnnotations,
          newLabel,
        });
        this.triggerTotalPulse();
      } catch (e) {
        // Slice 10a #2 — the store action rolls back the local commit on
        // save failure (UI matches DB again). Show a brief error toast so
        // the TC knows the swap didn't take and can retry. Auto-dismisses
        // on the same timeline as the undo toast (6s).
        //
        // Slice 10a #8 — for 409 CONCURRENT_EDIT, the conflict banner
        // takes over (more informative + actionable). Skip the toast to
        // avoid double-surfacing the same failure.
        const status = e && e.response && e.response.status;
        const payload = e && e.response && e.response.data;
        if (status === 409 && payload && payload.code === 'CONCURRENT_EDIT') {
          return;
        }
        console.warn('[TxPresenterPresentMode] swap save failed:', e && e.message);
        this.showSwapErrorToast(e && e.message);
      }
    },

    showSwapErrorToast(detail) {
      if (this.swapErrorTimer) clearTimeout(this.swapErrorTimer);
      this.swapError = detail
        ? `Could not save the swap (${detail}). Try again.`
        : 'Could not save the swap. Try again.';
      this.swapErrorTimer = setTimeout(() => { this.swapError = null; }, this.UNDO_TIMEOUT_MS);
    },

    dismissSwapErrorToast() {
      if (this.swapErrorTimer) {
        clearTimeout(this.swapErrorTimer);
        this.swapErrorTimer = null;
      }
      this.swapError = null;
    },

    showUndoToast({ previousAnnotations, newLabel }) {
      // Clear any existing toast first so a rapid double-swap shows the
      // most-recent action with a fresh 6s window.
      if (this.undoTimer) clearTimeout(this.undoTimer);
      this.undoState = { previousAnnotations, newLabel };
      this.undoTimer = setTimeout(() => { this.undoState = null; }, this.UNDO_TIMEOUT_MS);
    },

    async onUndoSwap() {
      const snapshot = this.undoState && this.undoState.previousAnnotations;
      this.dismissUndoToast();
      if (!snapshot) return;
      try {
        await this.$store.dispatch('txPresenter/undoAltSwap', { annotations: snapshot });
        this.triggerTotalPulse();
      } catch (e) { /* same logic as onSwapAlt — fail-silent */ }
    },

    dismissUndoToast() {
      if (this.undoTimer) {
        clearTimeout(this.undoTimer);
        this.undoTimer = null;
      }
      this.undoState = null;
    },

    triggerTotalPulse() {
      if (this.totalPulseTimer) clearTimeout(this.totalPulseTimer);
      this.totalPulsing = true;
      this.totalPulseTimer = setTimeout(() => {
        this.totalPulsing = false;
      }, this.TOTAL_PULSE_MS);
    },
  },
};
</script>

<style scoped>
.pm {
  --pm-bg: #f7f8fa;
  --pm-surface: #ffffff;
  --pm-border: #e4e7ec;
  --pm-text: #0f172a;
  --pm-text-mute: #475569;
  --pm-text-dim: #94a3b8;
  --pm-accent: #0d5f54;
  --pm-accent-soft: #e6f1ef;
  --pm-accent-ink: #094840;

  position: fixed;
  inset: 0;
  background: var(--pm-bg);
  color: var(--pm-text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  /* Above the app shell chrome ($shell-z-chrome 1020 in AppShell/_shell.scss: the sidebar, its
     64px iPad rail and the top bar), below $shell-z-popover 1050 and BootstrapVue modals (1050,
     portaled to body). At 50 the shell painted OVER Present Mode and the rail covered the left
     edge of the centred signature box on tablets: the consent checkbox sat at x~47-63 under a
     64px rail (client report 2026-10-20). Pinned by
     cypress/node-tests/tx-presenter-present-mode-above-shell.test.js. */
  z-index: 1025;
  overflow: hidden;
}
.pm * { box-sizing: border-box; }

.pm-state {
  margin: auto;
  text-align: center;
  color: var(--pm-text-mute);
}
.pm-state .fa {
  font-size: 36px;
  color: var(--pm-accent);
  margin-bottom: 14px;
}
.pm-state-error h2 {
  font-size: 22px;
  margin: 6px 0 8px;
  color: var(--pm-text);
}
.pm-state-error p {
  font-size: 14px;
  margin-bottom: 18px;
}

.pm-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 16px 28px;
  background: var(--pm-surface);
  border-bottom: 1px solid var(--pm-border);
  flex-shrink: 0;
}
.pm-topbar-left {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.pm-topbar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.pm-practice-logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--pm-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}
.pm-practice-logo-large {
  max-height: 44px;
  max-width: 220px;
  object-fit: contain;
  display: block;
}
.pm-practice-meta { min-width: 0; }
.pm-practice-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--pm-text);
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pm-practice-tag {
  font-size: 11px;
  color: var(--pm-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-top: 2px;
}

.pm-topbar-patient {
  min-width: 0;
}
.pm-patient-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--pm-text);
  letter-spacing: -0.2px;
}
.pm-plan-heading {
  font-size: 12px;
  color: var(--pm-text-dim);
  margin-top: 2px;
}

/* Photo Attachments — the patient-facing gallery button, sitting under the
   plan heading. Deliberately warmer than the TC chrome around it: the patient
   is meant to notice this one and ask to see their photos. */
.pm-photos-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 8px;
  padding: 7px 13px;
  border: 1px solid var(--pm-accent);
  background: var(--pm-accent-soft);
  color: var(--pm-accent);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s;
}
.pm-photos-btn:hover { background: var(--pm-surface); }
.pm-photos-btn .fa { font-size: 12px; }
.pm-photos-count {
  background: var(--pm-accent);
  color: #fff;
  border-radius: 9px;
  padding: 0 6px;
  font-size: 10.5px;
  line-height: 16px;
  min-width: 16px;
  text-align: center;
}

/* TC-side Attach Photos, matched to Print/Exit so the toolbar stays even. */
.pm-photos-attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid var(--pm-border);
  background: var(--pm-surface);
  color: var(--pm-accent);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
}
.pm-photos-attach-btn:hover {
  background: var(--pm-accent-soft);
  border-color: var(--pm-accent);
}
.pm-photos-attach-btn .fa { font-size: 12px; }

.pm-topbar-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  flex-shrink: 0;
}
.pm-topbar-actions {
  display: inline-flex;
  gap: 8px;
}
.pm-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid var(--pm-border);
  background: var(--pm-surface);
  color: var(--pm-accent);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
}
.pm-print-btn:hover {
  background: var(--pm-accent-soft);
  border-color: var(--pm-accent);
}
.pm-print-btn .fa { font-size: 12px; }
.pm-exit-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid var(--pm-border);
  background: var(--pm-surface);
  color: var(--pm-text-mute);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}
.pm-exit-btn:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}
.pm-exit-btn .fa { font-size: 12px; }

.pm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px;
  background: var(--pm-surface);
  border-bottom: 1px solid var(--pm-border);
  flex-shrink: 0;
}
.pm-lens-switcher {
  display: inline-flex;
  background: #f1f3f6;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}
.pm-lens-tab {
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: var(--pm-text-mute);
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}
.pm-lens-tab:hover { color: var(--pm-text); }
.pm-lens-tab.active {
  background: var(--pm-surface);
  color: var(--pm-accent-ink);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}

/* Plan totals — a two-column grid (label | amount) so every dollar figure
   right-aligns in a clean column. Lives in the topbar, right-aligned and in
   line with the patient name. */
.pm-total {
  display: grid;
  grid-template-columns: auto auto;
  align-items: baseline;
  column-gap: 18px;
  row-gap: 3px;
  padding: 8px 14px;
  background: var(--pm-accent-soft);
  border: 1px solid #b9dcd5;
  border-radius: 12px;
}
/* Labels (left column) */
.pm-total-k {
  text-align: left;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  font-weight: 600;
  color: var(--pm-accent-ink);
  opacity: 0.85;
}
/* Amounts (right column) — right-aligned, tabular so digits line up. */
.pm-total-v {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--pm-accent-ink);
  font-size: 12px;
}
/* Headline "Total" row — larger, full opacity. */
.pm-total-k-head { font-size: 10px; opacity: 1; }
.pm-total-v-head { font-size: 18px; letter-spacing: -0.3px; }
.pm-total-v-credit { font-weight: 500; opacity: 0.85; }
.pm-total-v-patient { font-size: 14px; }
.pm-total-v-monthly { font-weight: 500; opacity: 0.9; white-space: nowrap; }

.pm-canvas {
  flex: 1;
  overflow-y: auto;
  padding: 28px 40px 60px;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
}

/* Tablet portrait + narrow desktop */
@media (max-width: 900px) {
  .pm-topbar { padding: 14px 18px; }
  .pm-toolbar { padding: 10px 16px; }
  .pm-canvas { padding: 20px 18px 60px; }
}

/* iPad portrait (≈768px) — chairside tap comfort. */
@media (max-width: 768px) {
  .pm-print-btn,
  .pm-exit-btn { min-height: 44px; padding: 10px 16px; }
  .pm-lens-tab { min-height: 40px; padding: 10px 18px; }
}

/* Phones / very narrow — let the header wrap (totals drop below the brand,
   still right-aligned). Payments collapse to one column further down, in the
   breakpoint block that sits after the base .pm-payments-grid rule. */
@media (max-width: 560px) {
  .pm-topbar { flex-wrap: wrap; }
  .pm-topbar-right { margin-left: auto; }
}

/* ── Slice 9c — Lens crossfade ───────────────────────────────
   Mode 'out-in': old view fades to opacity 0 (180ms), then the new view
   fades from opacity 0 to 1 (220ms). Slight stagger feels less abrupt
   than a same-duration crossfade and avoids the layout overlap that
   would happen with mode='in-out' (each view has different vertical
   rhythm). */
.pm-lens-leave-active {
  transition: opacity 0.18s ease;
}
.pm-lens-enter-active {
  transition: opacity 0.22s ease;
}
.pm-lens-leave-to,
.pm-lens-enter-from,
.pm-lens-enter {
  opacity: 0;
}

/* ── Slice 9c — Privacy screen ───────────────────────────────
   Secondary FAB sits ABOVE the TC Controls FAB (which is 22px from
   bottom + 56px tall = 78px footprint; this one starts at 94px so
   there's a 16px gap). Smaller circle (48px) + slate color so it
   reads as secondary action. */
/* ── Slice 9d — Total fee pulse + undo toast ─────────────────
   Pulse: subtle 1s highlight on the total-fee pill so the eye lands on
   the financial change after an alt swap. NOT flashy — a brief glow,
   not an alarm. */
.pm-total-pulse {
  animation: pm-total-pulse-anim 1s ease-out;
}
@keyframes pm-total-pulse-anim {
  0%   { box-shadow: 0 0 0 0 rgba(13, 95, 84, 0.35); background: #b5d9d2; }
  100% { box-shadow: 0 0 0 8px rgba(13, 95, 84, 0); background: var(--pm-accent-soft); }
}

/* Undo toast — bottom-LEFT so it doesn't compete with the FABs on the
   right. Slate-themed to match the TC Controls tray (this is a TC
   action, not patient-facing). */
.pm-undo-toast {
  position: fixed;
  bottom: 28px;
  left: 28px;
  z-index: 62;  /* above FABs + tray panel */
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px 12px 16px;
  background: #1e293b;
  color: #f1f5f9;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.28), 0 2px 8px rgba(15, 23, 42, 0.18);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 13px;
  max-width: calc(100vw - 56px);
}
.pm-undo-toast > .fa { color: #5eead4; font-size: 14px; flex-shrink: 0; }
.pm-undo-text { line-height: 1.4; }
.pm-undo-text strong { color: #99f6e4; font-weight: 600; }
.pm-undo-btn {
  padding: 5px 12px;
  background: transparent;
  border: 1px solid #5eead4;
  color: #99f6e4;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.pm-undo-btn:hover { background: #5eead4; color: #0f172a; }
.pm-undo-close {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pm-undo-close:hover { background: #334155; color: #cbd5e1; }
.pm-undo-close .fa { font-size: 11px; }

.pm-undo-enter-active,
.pm-undo-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.pm-undo-enter-from, .pm-undo-leave-to,
.pm-undo-enter, .pm-undo-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Slice 10a #8 — Concurrent-edit conflict banner.
   Mirrors the Builder's banner shape; amber-themed so it reads as
   "two people working" rather than a hard error. Sits between top bar
   and toolbar, above the canvas. */
.pm-conflict-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 24px;
  background: #fef9f0;
  border-bottom: 1px solid #f1d8b1;
  color: #78350f;
  font-size: 13px;
  flex-shrink: 0;
}
.pm-conflict-banner > .fa { color: #b45309; font-size: 16px; flex-shrink: 0; }
.pm-conflict-body { flex: 1; min-width: 0; }
.pm-conflict-title { font-weight: 600; }
.pm-conflict-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #92400e;
}
.pm-conflict-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.pm-conflict-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #f1d8b1;
  background: #fff;
  color: #78350f;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.pm-conflict-btn:hover { background: #fef3c7; }
.pm-conflict-btn .fa { font-size: 11px; }
.pm-conflict-close {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #92400e;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pm-conflict-close:hover { background: rgba(180, 83, 9, 0.1); }
.pm-conflict-close .fa { font-size: 11px; }

/* Slice 10a #2 — swap save-failure toast. Same position + shape as the
   undo toast but red-themed. Sits 12px above the undo toast slot so the
   two can coexist in the rare case both fire near-simultaneously. */
.pm-swap-error-toast {
  position: fixed;
  bottom: 84px;
  left: 28px;
  z-index: 63;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px 12px 16px;
  background: #7f1d1d;
  color: #fee2e2;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.28), 0 2px 8px rgba(15, 23, 42, 0.18);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 13px;
  max-width: calc(100vw - 56px);
}
.pm-swap-error-toast > .fa { color: #fca5a5; font-size: 14px; flex-shrink: 0; }

/* ── Slice 9e — Payment options panel ────────────────────────
   Read-only chairside display of practice payment plans. Card grid
   matches patient view's spacing rhythm but without the interactive
   "I'm interested" buttons — TC walks the patient through verbally. */
.pm-payments {
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid var(--pm-border);
}
.pm-payments-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
}
.pm-payments-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 17px;
  font-weight: 500;
  color: var(--pm-accent);
  margin: 0;
}
.pm-payments-helper {
  font-size: 12.5px;
  color: var(--pm-text-mute);
  line-height: 1.5;
}
.pm-payments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
/* Breakpoints for the card grid live HERE, after the base rule: same specificity,
   so declaration order decides. They sat above it from 2026-05-24 to 2026-09-13 and
   never applied — iPad portrait showed 3 + 1 cards instead of 2 × 2. */
@media (max-width: 768px) {
  .pm-payments-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 560px) {
  .pm-payments-grid { grid-template-columns: 1fr; }
}
/* Flex column + .pm-payment-foot { margin-top: auto } so every card's price
   and CTA sit on the card's bottom edge — a row of cards with different
   amounts of prose (or a finance tier table) still lines its numbers up.
   position: relative stays: both badges are absolutely positioned to it. */
.pm-payment-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--pm-surface);
  border: 1px solid var(--pm-border);
  border-radius: 10px;
  padding: 14px 14px 12px;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  cursor: pointer;
  outline: none;
}
.pm-payment-foot { margin-top: auto; }
.pm-payment-card:hover { border-color: #b9dcd5; }
.pm-payment-card:focus-visible { box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.25); }
.pm-payment-card.recommended {
  border-color: #b9dcd5;
  box-shadow: 0 1px 3px rgba(13, 95, 84, 0.08);
}
.pm-payment-card.chosen {
  border-color: #0d9488;
  background: #ecf5f3;
  box-shadow: 0 0 0 1px #0d9488;
}
.pm-payment-chosen-badge {
  position: absolute;
  top: -9px;
  left: 12px;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  background: #0d9488;
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.pm-payment-chosen-badge .fa { font-size: 9px; }
/* The card's price block: headline amount + qualifying sub-line. Lives in the
   foot, directly above the CTA. */
.pm-payment-math {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 10px 0 0;
}
.pm-pay-math-amt {
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--pm-accent, #0d5f54);
  font-variant-numeric: tabular-nums;
}
.pm-pay-math-sub {
  font-size: 11px;
  color: var(--pm-text-mute);
}
/* Multi-tier financing menu — one row per term + estimated monthly. Shown
   under the card regardless of the money gate (the tiers ARE the offer). */
.pm-finance-tiers {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 6px 0;
}
.pm-finance-tier {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid var(--pm-border, #e4e7ec);
  font-size: 12.5px;
}
.pm-finance-tier:last-child { border-bottom: none; }
.pm-finance-tier-label { color: var(--pm-text-mute); }
.pm-finance-tier-amt {
  font-weight: 700;
  color: var(--pm-accent, #0d5f54);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pm-payment-cta {
  margin-top: 10px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 12px;
  border: 1px solid var(--pm-accent, #0d5f54);
  border-radius: 8px;
  background: #fff;
  color: var(--pm-accent, #0d5f54);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.pm-payment-cta:hover { background: #ecf5f3; }
.pm-payment-cta.active {
  background: var(--pm-accent, #0d5f54);
  color: #fff;
}
.pm-payment-cta .fa { font-size: 12px; }
.pm-payment-dates {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--pm-border);
}
.pm-payment-dates-caption {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--pm-text-dim);
  margin-bottom: 6px;
}
.pm-payment-date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}
.pm-payment-date-label {
  font-size: 11.5px;
  color: var(--pm-text-mute);
  min-width: 72px;
}
.pm-payment-date-input {
  flex: 1;
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid var(--pm-border);
  border-radius: 6px;
  color: var(--pm-text);
  background: #fff;
}
.pm-payment-badge {
  position: absolute;
  top: -9px;
  left: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--pm-accent);
  color: #fff;
  font-size: 9.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.pm-payment-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--pm-text);
  letter-spacing: -0.2px;
  margin-bottom: 4px;
}
.pm-payment-desc {
  font-size: 12px;
  color: var(--pm-text-mute);
  line-height: 1.5;
  margin-bottom: 6px;
}
.pm-payment-terms {
  font-size: 10.5px;
  color: var(--pm-text-dim);
  font-style: italic;
}

/* Additional Fee row on a payment card (read-only chairside; amount comes
   from the Builder per-patient override). Same amber palette as the Plan
   Summary fee row so the two visually link. */
.pm-payment-fee {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 6px 0 4px;
  padding: 6px 8px;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 6px;
  font-size: 11.5px;
  color: #78350f;
}
.pm-payment-fee .fa { font-size: 11px; color: #b45309; }
.pm-payment-fee-label { flex: 1; }
.pm-payment-fee-amt {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* "Pay today" downpayment row on the chosen card — slots in above the
   payment-date inputs. $-prefix input for chairside ergonomics. */
.pm-payment-paytoday-row {
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--pm-border);
}
.pm-payment-paytoday-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0; /* allow shrinking inside the narrow card */
}
.pm-payment-paytoday-prefix {
  position: absolute;
  left: 10px;
  font-size: 12px;
  color: var(--pm-text-mute);
  pointer-events: none;
}
.pm-payment-paytoday-input {
  flex: 1;
  min-width: 0;
  padding-left: 22px;
}

.pm-payments-enter-active,
.pm-payments-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.pm-payments-enter-from, .pm-payments-leave-to,
.pm-payments-enter, .pm-payments-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ─── In-office signature ────────────────────────────── */
.pm-sign {
  max-width: 760px;
  margin: 28px auto 0;
}
.pm-sign-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: -12px;  /* SignaturePanel has its own top margin */
}
.pm-sign-bar-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--pm-accent);
}
.pm-sign-bar-label .fa { margin-right: 6px; }
.pm-sign-cancel {
  border: 1px solid var(--pm-border);
  background: var(--pm-surface);
  color: var(--pm-text-mute);
  border-radius: 7px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.pm-sign-cancel:hover { background: #f1f3f6; color: var(--pm-text); }
.pm-sign-done {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 22px;
  background: #ecf5f3;
  border: 1px solid #b9dcd5;
  border-radius: 12px;
}
.pm-sign-done > .fa { font-size: 28px; color: #10b981; flex-shrink: 0; }
.pm-sign-done-body { flex: 1; min-width: 0; }
.pm-sign-done-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 16px;
  font-weight: 600;
  color: #0d5f54;
}
.pm-sign-done-sub { font-size: 12.5px; color: #475569; margin-top: 2px; }
.pm-sign-done-btn {
  border: 1px solid #0d5f54;
  background: #0d5f54;
  color: #fff;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}
.pm-sign-done-btn:hover { background: #094840; }

/* ─── Print ───────────────────────────────────────────
   The shell is position:fixed + overflow:hidden, which would clip the
   print to the viewport — override to a normal flowing document. Drop the
   chairside chrome (tray, toolbar, FABs, toasts); keep the practice/patient
   header context + the canvas (active view + Plan Summary + payments). */
@media print {
  .pm {
    position: static !important;
    inset: auto !important;
    overflow: visible !important;
    height: auto !important;
    background: #fff !important;
    z-index: auto !important;
  }
  .pm-canvas {
    overflow: visible !important;
    height: auto !important;
    padding: 0 !important;
  }
  .pm-no-print,
  .pm-topbar-right,
  /* The gallery button lives in the topbar-LEFT (under the plan heading), so
     it isn't covered by .pm-topbar-right. A button that opens a pop-over is
     meaningless on paper. The camera icons beside teeth are hidden too, but
     that rule has to live in the view components — a scoped selector here
     can't reach a child component's elements. */
  .pm-photos-btn,
  .pm-toolbar,
  .pm-conflict-banner,
  .pm-undo-toast,
  .pm-swap-error-toast,
  .pm-controls-fab { display: none !important; }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .pm-payment-card,
  .tps-summary { break-inside: avoid; }
}
</style>
